<template>
  <div class="select-table">
    <v-autocomplete
      v-model="modelData"
      ref="customAutocomplete"
      :items="filteredItems"
      dense
      :outlined="!solo"
      :solo="solo"
      hide-details
      chips
      deletable-chips
      :color="color"
      :clearable="clearable"
      :multiple="multiple"
      :filter="() => true"
      :item-text="itemTextKey"
      :close-on-select="false"
      :menu-props="{ closeOnContentClick: false }"
      :disabled="disabled"
      :error-messages="errorMessages"
      @blur="onBlur"
      :class="{
        'disabled-form': disabled,
      }"
      class="text-center custom-select custom-select-center"
      item-color="first"
      @input="setParentValue()"
      @update:menu="onMenuUpdate"
      :search-input.sync="searchQuery"
      @keydown.enter.prevent.stop="handleEnter"
    >
      <template #label>
        <label class="p-label">
          {{ label }}
          <span class="p-label__star" v-if="isRequired">
            ({{ staticLabel.validation.required }})
          </span>
        </label>
      </template>

      <template v-slot:append>
        <v-icon class="chevron-icon" size="14pt">mdi-chevron-down</v-icon>
      </template>

      <template v-slot:selection="{ item, index }">
        <div
          v-if="multiple && modelData?.length > 2 && index === 2"
          class="text-p-muted-light"
        >
          <strong class="p-fs-xxs">
            {{ modelData.length - 2 }}
          </strong>
          <span class="p-fs-xxxs">
            {{ staticLabel.caption.anotherCase }}
          </span>
        </div>
        <v-chip v-if="multiple && index < 2" :key="item[selectionKey]" outlined>
          {{ item[itemTextKey] ?? "" }}

          <v-icon class="ms-1" small @click.stop="removeItem(item)">
            mdi-close-circle
          </v-icon>
        </v-chip>

        <span
          v-if="!multiple && !isSearching"
          class="p-fs-xs text-center custom-select__text"
          style="position: absolute; pointer-events: none"
        >
          {{ item[itemTextKey] ?? "" }}
        </span>
      </template>

      <template v-slot:prepend-item>
        <v-row class="align-center header-row-grid mx-0 mb-0">
          <v-col
            v-if="multiple"
            :cols="getCheckboxColSize()"
            class="pa-0"
          ></v-col>

          <v-col
            v-for="(column, index) in columns"
            :key="column.key || index"
            :cols="getColSize(index)"
            class="text-center p-fs-xs bordered-col p-pointer pa-0"
            @click.stop.prevent="sortByColumn(column.key)"
          >
            <p class="mb-0">
              {{ column.name }}
              <span v-if="sortColumn === column.key">
                {{ sortAsc ? "▲" : "▼" }}
              </span>
            </p>
          </v-col>
        </v-row>
      </template>

      <template v-slot:item="{ item, attrs }">
        <v-row
          v-bind="attrs"
          class="custom-item ma-0 p-pointer"
          v-on="
            multiple
              ? {
                  click: (e) => {
                    e.stopPropagation();
                    toggleItem(item);
                  },
                }
              : {}
          "
        >
          <v-col
            v-if="multiple"
            :cols="getCheckboxColSize()"
            class="py-0 bordered-col custom-checkbox d-flex justify-center"
          >
            <v-checkbox
              :id="'chk-' + item[selectionKey]"
              :input-value="isSelected(item)"
              hide-details
              :color="checkboxColor"
              class="mt-0 pt-0"
            />
          </v-col>

          <v-col
            v-for="(column, index) in columns"
            :key="column.key || index"
            :cols="getColSize(index)"
            class="text-start p-fs-xs bordered-col d-flex align-center"
          >
            <label
              :for="'chk-' + item[selectionKey]"
              :title="item[column.key]"
              class="custom-item__text"
            >
              {{ item[column.key] }}
            </label>
          </v-col>
        </v-row>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
import staticLabel from "@/lang/StaticLabels.js";

export default {
  name: "SelectGrid",
  props: {
    modelValue: { default: null },
    label: { type: String, default: "" },
    items: { type: Array, required: true },
    columns: { type: Array, required: true },
    color: { type: String, default: "first" },
    multiple: { type: Boolean, default: false },
    isRequired: { type: Boolean, default: false },
    errorMessages: { type: [String, Array], default: () => [] },
    clearable: { type: Boolean, default: true },
    columnSizes: { type: Array, default: () => [] },
    itemTextKey: { type: String, default: "text" },
    selectionKey: { type: String, default: "value" },
    checkboxColor: { type: String, default: "first" },
    disabled: { type: Boolean, default: false },
    dialogVisible: { type: Boolean, default: true },
    solo: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      staticLabel,
      searchQuery: "",
      sortAsc: true,
      modelData: null,
      sortColumn: null,
    };
  },
  watch: {
    modelValue: {
      handler() {
        this.setValue();
      },
      immediate: true,
    },

    items: {
      handler() {
        this.setValue();
      },
      deep: true,
    },

    dialogVisible(val) {
      if (!val) {
        this.resetSearch();
      }
    },
  },
  computed: {
    isSearching() {
      return !!String(this.searchQuery ?? "").length;
    },
    filteredItems() {
      let items = this.items;
      if (this.searchQuery) {
        const q = String(this.searchQuery).toLowerCase();
        items = items.filter((item) =>
          this.columns.some((col) => {
            const val = item[col.key];
            return val && String(val).toLowerCase().includes(q);
          }),
        );
      }
      if (this.sortColumn) {
        items = [...items].sort((a, b) => {
          const valA = a[this.sortColumn];
          const valB = b[this.sortColumn];
          if (valA == null) return 1;
          if (valB == null) return -1;
          if (typeof valA === "number" && typeof valB === "number") {
            return this.sortAsc ? valA - valB : valB - valA;
          }
          return this.sortAsc
            ? String(valA).localeCompare(String(valB))
            : String(valB).localeCompare(String(valA));
        });
      }
      return items;
    },
  },
  methods: {
    setValue() {
      if (this.multiple) {
        this.modelData = Array.isArray(this.modelValue)
          ? this.items.filter((i) =>
              this.modelValue.includes(i[this.selectionKey]),
            )
          : [];
      } else {
        this.modelData = this.modelValue;
      }
    },
    handleEnter() {
      this.$emit("enter-pressed");
    },
    setParentValue() {
      //  const searchItem = this.filteredItems.find(
      //     (e) => e.value === this.modelData
      //   );
      //   if (searchItem) {
      //     this.$emit("update:modelValue", searchItem);
      //   } else {
      //     this.$emit("update:modelValue", null);
      //   }
      if (this.multiple) {
        const selected = Array.isArray(this.modelData)
          ? this.modelData.map((val) => {
              const found = this.items.find(
                (i) =>
                  i[this.selectionKey] === (val?.[this.selectionKey] ?? val),
              );
              return found ? found[this.selectionKey] : val;
            })
          : [];

        this.$emit("update:modelValue", selected);
      } else {
        const searchItem = this.filteredItems.find(
          (e) => e[this.selectionKey] === this.modelData,
        );
        this.$emit("update:modelValue", searchItem ? searchItem : null);
      }
      this.searchQuery = "";
    },
    onBlur(event) {
      this.resetSearch();
      this.$emit("blur", event);
    },
    resetSearch() {
      this.searchQuery = "";

      this.$nextTick(() => {
        const ac = this.$refs.customAutocomplete;

        if (ac) {
          ac.internalSearch = "";
          ac.lazySearch = "";

          // force rerender input
          if (ac.$refs.input) {
            ac.$refs.input.value = "";
          }
        }
      });
    },

    onMenuUpdate(isOpen) {
      if (!isOpen) {
        this.resetSearch();
      }
    },

    sortByColumn(columnKey) {
      if (this.sortColumn === columnKey) this.sortAsc = !this.sortAsc;
      else {
        this.sortColumn = columnKey;
        this.sortAsc = true;
      }
    },
    // updateSearchQuery(query) {
    //   this.searchQuery = query;
    // },
    removeItem(item) {
      if (this.multiple) {
        this.modelData = this.modelData.filter(
          (v) => v[this.selectionKey] !== item[this.selectionKey],
        );
      } else {
        this.modelData = null;
      }

      this.setParentValue();
    },
    toggleItem(item) {
      if (!this.multiple) {
        this.modelData = item;
        this.setParentValue();
        return;
      }

      const exists = this.isSelected(item);

      if (exists) {
        this.modelData = this.modelData.filter(
          (v) => v[this.selectionKey] !== item[this.selectionKey],
        );
      } else {
        this.modelData = [...(this.modelData || []), item];
      }

      this.setParentValue();
    },

    isSelected(item) {
      // if (this.multiple) {
      //   return (
      //     Array.isArray(this.internalValue) &&
      //     this.internalValue.some(
      //       (v) => v[this.selectionKey] === item[this.selectionKey]
      //     )
      //   );
      // } else {
      //   return (
      //     this.internalValue?.[this.selectionKey] === item[this.selectionKey]
      //   );
      // }

      if (this.multiple) {
        return (
          Array.isArray(this.modelData) &&
          this.modelData.some(
            (v) => v[this.selectionKey] === item[this.selectionKey],
          )
        );
      } else {
        return this.modelData?.[this.selectionKey] === item[this.selectionKey];
      }
    },
    getCheckboxColSize() {
      const cs = this.columnSizes || [];
      const v = Number(cs[0]);
      return Number.isFinite(v) && v > 0 ? v : 1;
    },
    getColSize(index) {
      const cs = this.columnSizes || [];
      const offset = this.multiple ? 1 : 0;
      const provided = cs[index + offset];
      if (provided !== undefined && !Number.isNaN(Number(provided))) {
        const n = Number(provided);
        if (n > 0) return n;
      }
      const checkbox = this.multiple ? this.getCheckboxColSize() : 0;
      const remaining = Math.max(1, 12 - checkbox);
      return Math.max(
        1,
        Math.floor(remaining / Math.max(1, this.columns.length)),
      );
    },
    // resetSearch() {
    //   this.searchQuery = "";

    //   this.$nextTick(() => {
    //     if (this.$refs.autocomplete) {
    //       this.$refs.autocomplete.lazySearch = "";
    //       this.$refs.autocomplete.internalSearch = "";
    //     }
    //   });
    // },
  },
};
</script>

<style lang="css">
.v-list {
  padding-bottom: 0 !important;
  /* padding-top: 18px !important; */
}

.custom-select .v-select__selections input {
  font-size: var(--font-size-xsmall);
}
</style>
